import { useEffect, useState } from "react";
import TopNavBar from "../../components/reusables/topNavBar";
import { toast } from "react-toastify";
import { getAuthToken } from "../../auth/authService";

const NOTIFICATIONS_URL = process.env.REACT_APP_NOTIFICATIONS_URL || 'http://localhost:8082';

const GROUPS = [
    {
        label: "Pedidos",
        events: [
            { key: "order.status_changed", label: "Status do pedido atualizado" },
            { key: "order.canceled", label: "Pedido cancelado" },
            { key: "order.ready_to_deliver", label: "Pedido pronto para entrega" },
        ]
    },
    {
        label: "Reservas",
        events: [
            { key: "reservation.created", label: "Reserva criada" },
            { key: "reservation.canceled", label: "Reserva cancelada" },
            { key: "reservation.released", label: "Reserva liberada" },
        ]
    },
    {
        label: "Alunos & Responsáveis",
        events: [
            { key: "student.created", label: "Aluno cadastrado" },
            { key: "student.low_balance", label: "Saldo baixo do aluno" },
            { key: "student.negative_balance", label: "Saldo negativo do aluno" },
            { key: "parent.created", label: "Responsável cadastrado" },
        ]
    },
    {
        label: "Pagamentos",
        events: [
            { key: "payment.confirmed", label: "Pagamento confirmado" },
            { key: "payment.failed", label: "Pagamento não aprovado" },
            { key: "payment.overdue", label: "Pagamento em atraso" },
            { key: "payment.refunded", label: "Reembolso processado" },
            { key: "payment.chargeback", label: "Chargeback recebido" },
        ]
    },
    {
        label: "Estoque",
        events: [
            { key: "product.low_stock", label: "Estoque baixo de produto" },
            { key: "recipe.low_stock", label: "Estoque baixo de receita" },
        ]
    },
    {
        label: "Assinaturas",
        events: [
            { key: "subscription.created", label: "Assinatura criada" },
            { key: "subscription.canceled", label: "Assinatura cancelada" },
        ]
    },
    {
        label: "Escola",
        events: [
            { key: "school.onboarding.approved", label: "Onboarding aprovado" },
            { key: "school.onboarding.rejected", label: "Onboarding recusado" },
        ]
    },
    {
        label: "Sistema",
        events: [
            { key: "user.email_missing", label: "Cadastro sem e-mail / notificação não enviada por falta de e-mail" },
            { key: "notification.email_failed", label: "Falha ao enviar e-mail" },
        ]
    },
];

function Toggle({ checked, onChange, disabled }) {
    return (
        <button
            type="button"
            onClick={onChange}
            disabled={disabled}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors
                ${checked ? 'bg-custom_blue' : 'bg-gray-300'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                    ${checked ? 'translate-x-6' : 'translate-x-1'}`}
            />
        </button>
    );
}

function NotificationsTab() {
    const [preferences, setPreferences] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    const clientCode = sessionStorage.getItem('clientCode');
    const userId = sessionStorage.getItem('userId');
    const token = getAuthToken();

    useEffect(() => {
        if (!clientCode || !userId || !token) return;
        fetch(
            `${NOTIFICATIONS_URL}/api/v1/notifications/preferences?clientCode=${encodeURIComponent(clientCode)}&userId=${encodeURIComponent(userId)}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        )
            .then(r => r.json())
            .then(setPreferences)
            .catch(() => toast.error("Erro ao carregar preferências de notificação."));
    }, [clientCode, userId, token]);

    const handleToggle = async (eventType) => {
        if (savingKey || !token) return;
        const newVal = !preferences[eventType];
        setPreferences(prev => ({ ...prev, [eventType]: newVal }));
        setSavingKey(eventType);
        try {
            const res = await fetch(
                `${NOTIFICATIONS_URL}/api/v1/notifications/preferences?clientCode=${encodeURIComponent(clientCode)}&userId=${encodeURIComponent(userId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ [eventType]: newVal })
                }
            );
            if (!res.ok) throw new Error();
        } catch {
            toast.error("Erro ao salvar preferência.");
            setPreferences(prev => ({ ...prev, [eventType]: !newVal }));
        } finally {
            setSavingKey(null);
        }
    };

    if (!preferences) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-custom_gray_font animate-pulse">Carregando preferências...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-5">
            <p className="text-sm text-custom_gray_font">
                Escolha quais tipos de notificações deseja receber. As alterações são salvas automaticamente.
            </p>
            {GROUPS.map(group => (
                <div key={group.label} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-700 text-sm">{group.label}</h3>
                    </div>
                    <div className="divide-y divide-gray-50">
                        {group.events.map(ev => (
                            <div key={ev.key} className="flex items-center justify-between px-5 py-3.5">
                                <span className="text-sm text-gray-700">{ev.label}</span>
                                <Toggle
                                    checked={!!preferences[ev.key]}
                                    onChange={() => handleToggle(ev.key)}
                                    disabled={savingKey === ev.key}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function UserNotificationPreferencesPage() {
    const [activeTab, setActiveTab] = useState('notifications');

    return (
        <div className='h-full flex flex-col'>
            <TopNavBar title={"Preferências de Usuário"} subtitle={""} />

            <div className="flex flex-col gap-6 mx-4 md:mx-16 my-6">
                <div className="flex border-b mb-4 sticky top-0 bg-gray-50 z-20 pt-2">
                    <button
                        className={`px-4 py-2 font-semibold transition-colors ${activeTab === 'notifications' ? 'border-b-2 border-custom_blue text-custom_blue' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('notifications')}
                    >
                        Notificações
                    </button>
                </div>

                <div className="min-h-[50vh]">
                    {activeTab === 'notifications' && <NotificationsTab />}
                </div>
            </div>
        </div>
    );
}

export default UserNotificationPreferencesPage;
